import React, { Fragment, memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { ATIVO_MERCADO } from '@investirapri/financa.js/lib/enums';
import {
  Divider,
  SearchFilterMultiSelect,
  SearchFilterRange,
  SearchFilterSingleSelect,
  SearchFiltersSection,
  Stack,
  Typography
} from 'investira.react.components';
import { SearchFiltersWrapper } from '../../../components/organisms';
import { ROUTES_MERCADOS } from '../../../enums';
import { ATIVOS_FILTER_CONTROL, getAtivosFilterConfig } from '../../../utils/ativosFilterRegistry';
import {
  readAtivosFilterQuery,
  removePaginationFromPath,
  serializeAtivosFilterQuery,
  updateAtivosFilterQuery
} from '../_shared/ativosFilterQuery';

const wFilterSections = [
  {
    id: 'classificacao',
    title: 'Tipo / Estratégia',
    fields: ['ativo_tipo_id', 'top_setor_id']
  },
  {
    id: 'caracteristicas',
    title: 'Características',
    fields: [
      // Reavaliar futuramente se a adaptação à Resolução CVM 175 deve voltar à interface.
      // 'cvm_175',
      'situacao',
      'exclusivo',
      'fechado',
      'longo',
      'cotas',
      'condominio_fechado',
      'responsabilidade_limitada',
      'qualificado',
      'profissional',
      'publico_alvo',
      'distribuicao',
      'previdenciario',
      'estruturado',
      'esg'
    ]
  },
  {
    id: 'custos-liquidez',
    title: 'Custos e condições',
    fields: ['taxa_adm', 'aplicacao_min', 'resgate_carencia']
  },
  {
    id: 'desempenho-risco',
    title: 'Desempenho e risco',
    fields: [
      'benchmark_indicador_id',
      'retorno',
      'perc_dias_acima_benchmark',
      'perc_meses_acima_benchmark',
      'sharpe',
      'tracking_error',
      'volatilidade'
    ]
  },
  {
    id: 'patrimonio-escala',
    title: 'Patrimônio e cotistas',
    groups: [
      { id: 'fundo', title: 'Fundo', fields: ['pl', 'cotistas'] },
      { id: 'gestor', title: 'Gestor', fields: ['gestor_pl', 'gestor_cotistas'] },
      { id: 'administrador', title: 'Administrador', fields: ['adm_pl', 'adm_cotistas'] }
    ]
  }
];

/** Converte um CSV da URL nos valores tipados esperados pelo multiselect. */
function pvReadCsvValues(pValue, pOptions) {
  const xValues = String(pValue || '')
    .split(',')
    .filter(Boolean);

  return xValues.reduce((pSelected, pValueItem) => {
    const xOption = pOptions.find(pOption => String(pOption.value) === pValueItem);

    if (xOption) {
      pSelected.push(xOption.value);
    }

    return pSelected;
  }, []);
}

/** Monta os valores controlados dos filtros a partir da query normalizada. */
function pvCreateFilterValues(pQuery, pFields) {
  return pFields.reduce((pValues, pFilter) => {
    const xValue = pQuery[pFilter.name];

    if (pFilter.control === ATIVOS_FILTER_CONTROL.MULTI_SELECT) {
      pValues[pFilter.name] = pvReadCsvValues(xValue, pFilter.options);
    } else if (pFilter.control === ATIVOS_FILTER_CONTROL.NUMERIC) {
      pValues[pFilter.name] = xValue ?? null;
    } else {
      pValues[pFilter.name] = xValue ?? '';
    }

    return pValues;
  }, {});
}

/** Cria o rascunho vazio de acordo com o controle de cada campo. */
function pvCreateEmptyFilterValues(pFields) {
  return pFields.reduce((pValues, pFilter) => {
    pValues[pFilter.name] =
      pFilter.control === ATIVOS_FILTER_CONTROL.MULTI_SELECT
        ? []
        : pFilter.control === ATIVOS_FILTER_CONTROL.NUMERIC
        ? null
        : '';

    return pValues;
  }, {});
}

/** Conta os campos que possuem valor efetivamente aplicado na URL. */
function pvCountAppliedFilters(pValues, pFields) {
  return pFields.filter(pFilter => {
    const xValue = pValues[pFilter.name];

    return Array.isArray(xValue) ? xValue.length > 0 : xValue !== '' && xValue != null;
  }).length;
}

/** Converte o valor controlado para o contrato numérico anulável. */
function pvToNumber(pValue) {
  if (pValue === '' || pValue === null || typeof pValue === 'undefined') {
    return null;
  }

  const xValue = Number(pValue);

  return Number.isFinite(xValue) ? xValue : null;
}

/**
 * Resolve o mercado pela rota e adapta seus filtros à família SearchFilters.
 * A URL permanece como fonte dos valores aplicados e o estado local guarda o rascunho.
 *
 * @param {Object} pProps Propriedades recebidas pelo slot de filtros da listagem.
 * @param {'desktop'|'mobile'} pProps.displayMode Apresentação escolhida pelo template.
 * @returns {JSX.Element|null} Filtros configurados para o mercado atual.
 */
const AtivosMercadoFilter = memo(pProps => {
  const { displayMode } = pProps;
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const xPathnameBase = xLocation.pathname.split('/')[2];
  const xMercado = ROUTES_MERCADOS[xPathnameBase]?.id || null;
  const xConfig = getAtivosFilterConfig(xMercado);
  const xFields = xConfig?.fields || [];
  const xQuery = readAtivosFilterQuery(xLocation.search, xMercado);
  const xAppliedValues = pvCreateFilterValues(xQuery, xFields);
  const xAppliedValuesKey = JSON.stringify(xAppliedValues);
  const [draftValues, setDraftValues] = useState(() => xAppliedValues);
  const [filterValidity, setFilterValidity] = useState({});
  const [open, setOpen] = useState(false);
  const xApplyDisabled = Object.values(filterValidity).some(pValid => pValid === false);
  const xAppliedCount = pvCountAppliedFilters(xAppliedValues, xFields);

  /** Sincroniza o rascunho quando a rota ou os filtros aplicados mudarem. */
  useEffect(() => {
    setDraftValues(JSON.parse(xAppliedValuesKey));
    setFilterValidity({});
  }, [xAppliedValuesKey, xMercado]);

  if (!xConfig) {
    return null;
  }

  /** Atualiza somente o campo editado no rascunho local. */
  function pvChangeFilter(pFilter, pValue) {
    setDraftValues(pCurrentValues => ({ ...pCurrentValues, [pFilter.name]: pValue }));
  }

  /** Registra a validade numérica sem provocar renderizações redundantes. */
  function pvChangeValidity(pName, pValid) {
    setFilterValidity(pCurrentValidity => {
      if (pCurrentValidity[pName] === pValid) {
        return pCurrentValidity;
      }

      return { ...pCurrentValidity, [pName]: pValid };
    });
  }

  /** Limpa somente o rascunho; a URL muda quando o usuário aplicar. */
  function pvClearDraftFilters() {
    setDraftValues(pvCreateEmptyFilterValues(xFields));
    setFilterValidity({});
  }

  /** Abre o diálogo mobile a partir do último estado aplicado. */
  function pvOpenMobileFilters() {
    setDraftValues(pvCreateFilterValues(xQuery, xFields));
    setFilterValidity({});
    setOpen(true);
  }

  /** Fecha o diálogo mobile e descarta alterações não aplicadas. */
  function pvCloseMobileFilters() {
    setDraftValues(pvCreateFilterValues(xQuery, xFields));
    setFilterValidity({});
    setOpen(false);
  }

  /** Serializa o rascunho completo, preserva a pesquisa e retorna à primeira página. */
  function pvApplyDraftFilters() {
    if (xApplyDisabled) {
      return;
    }

    const xUpdates = xFields.reduce((pUpdates, pFilter) => {
      const xValue = draftValues[pFilter.name];

      pUpdates[pFilter.name] =
        pFilter.control === ATIVOS_FILTER_CONTROL.MULTI_SELECT
          ? xValue.length
            ? xValue.join(',')
            : null
          : xValue;

      return pUpdates;
    }, {});
    const xNextQuery = updateAtivosFilterQuery(xQuery, xUpdates, xConfig.mercado);
    const xSearch = serializeAtivosFilterQuery(xNextQuery, xConfig.mercado);
    const xPathname = removePaginationFromPath(xLocation.pathname);

    setOpen(false);
    xNavigate(`${xPathname}${xSearch}`);
  }

  /** Renderiza o controle correspondente ao contrato do registry. */
  function pvRenderFilter(pFilter) {
    const xId = `ativos-${pFilter.name}`;
    const xValue = draftValues[pFilter.name];

    if (pFilter.control === ATIVOS_FILTER_CONTROL.MULTI_SELECT) {
      return (
        <SearchFilterMultiSelect
          id={xId}
          label={pFilter.label}
          value={Array.isArray(xValue) ? xValue : []}
          options={pFilter.options}
          visibleOptionCount={pFilter.name === 'ativo_tipo_id' ? 6 : undefined}
          optionsDialogTitle={pFilter.label}
          onChange={pValue => pvChangeFilter(pFilter, pValue)}
        />
      );
    }

    if (pFilter.control === ATIVOS_FILTER_CONTROL.SINGLE_SELECT) {
      return (
        <SearchFilterSingleSelect
          id={xId}
          label={pFilter.label}
          value={xValue}
          options={pFilter.options}
          onChange={pValue => pvChangeFilter(pFilter, pValue)}
        />
      );
    }

    return (
      <SearchFilterRange
        id={xId}
        label={pFilter.label}
        mode={pFilter.direction === 'min' ? 'min-only' : 'max-only'}
        showSlider={Boolean(pFilter.showSlider)}
        value={{
          min: pFilter.direction === 'min' ? pvToNumber(xValue) : null,
          max: pFilter.direction === 'max' ? pvToNumber(xValue) : null
        }}
        min={pFilter.minimum}
        max={pFilter.maximum}
        step={pFilter.step ?? (pFilter.decimalScale === 0 ? 1 : 0.01)}
        sliderScale={pFilter.sliderScale}
        currency={pFilter.currency}
        prefix={pFilter.prefix}
        suffix={pFilter.suffix}
        onChange={pValue =>
          pvChangeFilter(pFilter, pValue[pFilter.direction === 'min' ? 'min' : 'max'])
        }
        onValidityChange={pValid => pvChangeValidity(pFilter.name, pValid)}
      />
    );
  }

  /** Renderiza uma lista de filtros com divisores internos. */
  function pvRenderFilterList(pFilters) {
    return pFilters.map((pFilter, pIndex) => (
      <Fragment key={pFilter.name}>
        {pIndex > 0 && <Divider />}
        {pvRenderFilter(pFilter)}
      </Fragment>
    ));
  }

  return (
    <SearchFiltersWrapper
      displayMode={displayMode}
      id="ativos-filtros"
      title="Perfil"
      appliedCount={xAppliedCount}
      open={open}
      applyDisabled={xApplyDisabled}
      onApply={pvApplyDraftFilters}
      onClear={pvClearDraftFilters}
      onOpen={pvOpenMobileFilters}
      onClose={pvCloseMobileFilters}>
      <Stack sx={{ minWidth: 0, px: 0, pb: 1 }}>
        {wFilterSections.map(pSection => {
          const xSectionGroups = (pSection.groups || [pSection])
            .map(pGroup => ({
              ...pGroup,
              filters: pGroup.fields
                .map(pName => xFields.find(pFilter => pFilter.name === pName))
                .filter(Boolean)
            }))
            .filter(pGroup => pGroup.filters.length);
          const xSectionFields = xSectionGroups.reduce(
            (pFields, pGroup) => [...pFields, ...pGroup.filters],
            []
          );

          if (!xSectionFields.length) {
            return null;
          }

          const xSectionAppliedCount = pvCountAppliedFilters(xAppliedValues, xSectionFields);

          return (
            <SearchFiltersSection
              key={pSection.id}
              id={`ativos-${pSection.id}`}
              activeFiltersCount={xSectionAppliedCount}
              title={
                pSection.id === 'classificacao' && xMercado !== ATIVO_MERCADO.FND.id
                  ? 'Tipo / Setor'
                  : pSection.title
              }>
              {pSection.groups
                ? xSectionGroups.map((pGroup, pIndex) => {
                    const xGroupTitleId = `ativos-${pSection.id}-${pGroup.id}-title`;

                    return (
                      <Fragment key={pGroup.id}>
                        {pIndex > 0 && <Divider />}
                        <Stack
                          aria-labelledby={xGroupTitleId}
                          role="group"
                          sx={{ gap: 1, minWidth: 0 }}>
                          <Typography
                            id={xGroupTitleId}
                            color="text.secondary"
                            fontWeight={600}
                            variant="subtitle2">
                            {pGroup.title}
                          </Typography>
                          {pvRenderFilterList(pGroup.filters)}
                        </Stack>
                      </Fragment>
                    );
                  })
                : pvRenderFilterList(xSectionFields)}
            </SearchFiltersSection>
          );
        })}
      </Stack>
    </SearchFiltersWrapper>
  );
});

AtivosMercadoFilter.propTypes = {
  displayMode: PropTypes.oneOf(['desktop', 'mobile']).isRequired
};

AtivosMercadoFilter.displayName = 'AtivosMercadoFilter';

export default AtivosMercadoFilter;
